// src/Components/Modules/HR/RequisitionApproval.js
import React, { useState, useEffect, useMemo } from "react";
import { AxiosAuthInstance } from "../../../AxiosInterceptors";
import Breadcrumb from "../../Partials/Breadcrumb";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Skeleton,
  Alert,
  Stack,
  Avatar,
  TextField,
  InputAdornment,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from "@mui/material";
import {
  FaBoxOpen,
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
  FaSearch,
  FaFilter,
  FaSync,
  FaEye,
  FaUser,
  FaBuilding,
  FaExclamationTriangle,
  FaCheck,
  FaTimes,
  FaTruckLoading,
} from "react-icons/fa";
import { styled } from "@mui/material/styles";
import Swal from "sweetalert2";

// Styled Components
const GradientPaper = styled(Paper)(({ theme }) => ({
  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  color: "white",
  padding: theme.spacing(4),
  borderRadius: theme.spacing(3),
  marginBottom: theme.spacing(3),
  boxShadow: "0 10px 40px rgba(102, 126, 234, 0.3)",
}));

const StatCard = styled(Card)(({ theme, status }) => {
  const colors = {
    total: { bg: "#e3f2fd", color: "#1565c0", icon: "#2196f3" },
    pending: { bg: "#fff3e0", color: "#ef6c00", icon: "#ff9800" },
    approved: { bg: "#e8f5e9", color: "#2e7d32", icon: "#4caf50" },
    fulfilled: { bg: "#f3e5f5", color: "#7b1fa2", icon: "#9c27b0" },
  };
  const palette = colors[status] || colors.total;
  return {
    background: palette.bg,
    borderRadius: theme.spacing(2),
    textAlign: "center",
    padding: theme.spacing(2),
    transition: "all 0.3s ease",
    "&:hover": {
      transform: "translateY(-5px)",
      boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
    },
  };
});

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: theme.spacing(2),
  boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
  border: `1px solid ${theme.palette.divider}`,
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette.text.primary,
  borderBottom: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(2),
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
  transition: "background-color 0.2s ease",
}));

const ActionButton = styled(Button)(({ theme, variant }) => ({
  borderRadius: 2,
  textTransform: "none",
  fontWeight: 600,
  padding: theme.spacing(0.6, 1.5),
  minWidth: "auto",
  ...(variant === "approve" && {
    background: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
    color: "#1b5e20",
    "&:hover": {
      background: "linear-gradient(135deg, #38f9d7 0%, #43e97b 100%)",
      boxShadow: "0 4px 12px rgba(67, 233, 123, 0.3)",
    },
  }),
  ...(variant === "reject" && {
    background: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
    color: "#b71c1c",
    "&:hover": {
      background: "linear-gradient(135deg, #fee140 0%, #fa709a 100%)",
      boxShadow: "0 4px 12px rgba(250, 112, 154, 0.3)",
    },
  }),
  ...(variant === "fulfill" && {
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: "white",
    "&:hover": {
      background: "linear-gradient(135deg, #764ba2 0%, #667eea 100%)",
      boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)",
    },
  }),
}));

const RequisitionApproval = () => {
  const [requisitions, setRequisitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  // Modal States
  const [actionModal, setActionModal] = useState({ open: false, type: "", data: null });
  const [actionForm, setActionForm] = useState({ quantity: "", reason: "", notes: "" });

  useEffect(() => {
    fetchRequisitions();
  }, []);

  const fetchRequisitions = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await AxiosAuthInstance.get("hr/requisitions");
      setRequisitions(Array.isArray(res.data.data) ? res.data.data : []);
    } catch (err) {
      setError("Failed to load requisitions");
      setRequisitions([]);
    } finally {
      setLoading(false);
    }
  };

  const openActionModal = (type, row) => {
    setActionForm({
      quantity: type === "approve" ? row.requested_quantity : (row.approved_quantity || row.requested_quantity),
      reason: "",
      notes: "",
    });
    setActionModal({ open: true, type, data: row });
  };

  const submitAction = async () => {
    const { type, data } = actionModal;

    // ✅ If rejecting, ask for reason
    if (type === 'reject') {
      const { value: reason } = await Swal.fire({
        title: "Reject Requisition",
        input: 'textarea',
        inputLabel: 'Rejection Reason *',
        inputPlaceholder: 'Please provide a reason for rejection...',
        inputAttributes: {
          'aria-label': 'Rejection reason',
          'maxlength': 500
        },
        showCancelButton: true,
        confirmButtonColor: "#f44336",
        cancelButtonColor: "#757575",
        confirmButtonText: "Reject",
        cancelButtonText: "Cancel",
        inputValidator: (value) => {
          if (!value) {
            return 'You need to provide a rejection reason!';
          }
        }
      });

      if (!reason) return;

      setActionLoading(data.id);
      try {
        await AxiosAuthInstance.post(`hr/requisitions/${data.id}/reject`, {
          rejection_reason: reason,
        });
        Swal.fire({
          icon: "success",
          title: "Rejected!",
          text: "Requisition has been rejected.",
          timer: 2000,
          showConfirmButton: false,
        });
        setActionModal({ open: false, type: "", data: null });
        fetchRequisitions();
      } catch (err) {
        const errorMsg = err.response?.data?.message || "Failed to reject";
        Swal.fire({
          icon: "error",
          title: "Error",
          text: errorMsg,
        });
      } finally {
        setActionLoading(null);
      }
      return;
    }

    // ✅ For approve/fulfill, existing logic
    if (type === "approve" && (!actionForm.quantity || actionForm.quantity < 1)) {
      Swal.fire({ icon: "warning", title: "Invalid Quantity", text: "Please enter a valid approved quantity." });
      return;
    }

    const result = await Swal.fire({
      title: `Confirm ${type.charAt(0).toUpperCase() + type.slice(1)}`,
      html: `<p>Are you sure you want to <strong>${type}</strong> this requisition for <strong>${data.item_name}</strong>?</p>`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: type === "approve" ? "#4caf50" : "#667eea",
      cancelButtonColor: "#757575",
      confirmButtonText: `Yes, ${type.charAt(0).toUpperCase() + type.slice(1)}`,
    });

    if (result.isConfirmed) {
      setActionLoading(data.id);
      try {
        let payload = {};
        if (type === "approve") payload = { approved_quantity: Number(actionForm.quantity), admin_notes: actionForm.notes };
        if (type === "fulfill") payload = { fulfilled_quantity: Number(actionForm.quantity) };

        await AxiosAuthInstance.post(`hr/requisitions/${data.id}/${type}`, payload);

        Swal.fire({ icon: "success", title: "Success!", text: `Requisition ${type}ed successfully.`, timer: 2000, showConfirmButton: false });
        setActionModal({ open: false, type: "", data: null });
        fetchRequisitions();
      } catch (err) {
        const errorMsg = err.response?.data?.message || "Action failed";
        Swal.fire({ icon: "error", title: "Error", text: errorMsg });
      } finally {
        setActionLoading(null);
      }
    }
  };

  const filtered = useMemo(() => {
    if (!Array.isArray(requisitions)) return [];
    return requisitions.filter((item) => {
      const itemName = item.item_name?.toLowerCase() || "";
      const empName = item.employee?.employee_name?.toLowerCase() || "";
      const status = item.status?.toLowerCase() || "";
      const searchTerm = search.toLowerCase();

      const matchesSearch = itemName.includes(searchTerm) || empName.includes(searchTerm);
      const matchesFilter = statusFilter === "all" || status === statusFilter;

      return matchesSearch && matchesFilter;
    });
  }, [requisitions, search, statusFilter]);

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedData = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const stats = useMemo(() => {
    if (!Array.isArray(requisitions)) return { total: 0, pending: 0, approved: 0, fulfilled: 0 };
    return {
      total: requisitions.length,
      pending: requisitions.filter((r) => r.status === "pending").length,
      approved: requisitions.filter((r) => r.status === "approved").length,
      fulfilled: requisitions.filter((r) => r.status === "fulfilled").length,
    };
  }, [requisitions]);

  const getStatusChip = (status) => {
    const statusLower = status?.toLowerCase();
    const configs = {
      pending: { icon: <FaClock />, label: "Pending", bg: "#fff3e0", color: "#ef6c00", iconColor: "#ff9800" },
      approved: { icon: <FaCheckCircle />, label: "Approved", bg: "#e8f5e9", color: "#2e7d32", iconColor: "#4caf50" },
      rejected: { icon: <FaTimesCircle />, label: "Rejected", bg: "#ffebee", color: "#c62828", iconColor: "#f44336" },
      fulfilled: { icon: <FaCheckCircle />, label: "Fulfilled", bg: "#e3f2fd", color: "#1565c0", iconColor: "#2196f3" },
      cancelled: { icon: <FaTimesCircle />, label: "Cancelled", bg: "#f5f5f5", color: "#757575", iconColor: "#9e9e9e" },
    };
    const config = configs[statusLower] || configs.pending;
    return (
      <Chip icon={config.icon} label={config.label} size="small" sx={{ bgcolor: config.bg, color: config.color, fontWeight: 600, "& .MuiChip-icon": { color: config.iconColor } }} />
    );
  };

  const getUrgencyChip = (urgency) => {
    const configs = {
      high: { bg: "#ffebee", color: "#c62828", iconColor: "#f44336" },
      medium: { bg: "#fff3e0", color: "#ef6c00", iconColor: "#ff9800" },
      low: { bg: "#e8f5e9", color: "#2e7d32", iconColor: "#4caf50" },
    };
    const config = configs[urgency?.toLowerCase()] || configs.medium;
    return <Chip label={urgency || "Medium"} size="small" sx={{ bgcolor: config.bg, color: config.color, fontWeight: 600, textTransform: "capitalize" }} />;
  };

  if (loading && requisitions.length === 0) {
    return (
      <Box sx={{ p: 3, bgcolor: "#f5f7fa", minHeight: "100vh" }}>
        <Breadcrumb title="Requisition Approvals" />
        <GradientPaper><Skeleton variant="text" width="60%" height={60} /><Skeleton variant="text" width="40%" /></GradientPaper>
        <Grid container spacing={3}>{[1, 2, 3, 4].map((i) => <Grid item xs={12} sm={6} md={3} key={i}><Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} /></Grid>)}</Grid>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, bgcolor: "#f5f7fa", minHeight: "100vh" }}>
      <Breadcrumb title="Requisition Approvals" />

      {/* Header */}
      <GradientPaper elevation={0}>
        <Box sx={{ display: "flex", justifyContent: { xs: "center", sm: "space-between" }, alignItems: "center", flexWrap: "wrap", gap: 2 }}>
          <Box>
            <Typography variant="h4" fontWeight={700} gutterBottom><FaBoxOpen style={{ marginRight: 10, verticalAlign: "middle" }} /> Requisition Management</Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>Review, approve, and fulfill employee requisitions</Typography>
          </Box>
          <Tooltip title="Refresh Data"><IconButton onClick={fetchRequisitions} sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "white", "&:hover": { bgcolor: "rgba(255,255,255,0.3)" } }}><FaSync /></IconButton></Tooltip>
        </Box>
      </GradientPaper>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard status="total" elevation={0}><CardContent><FaBoxOpen size={32} color="#2196f3" style={{ marginBottom: 10 }} /><Typography variant="h3" fontWeight={700} color="#1565c0">{stats.total}</Typography><Typography variant="body2" color="#1565c0" fontWeight={600}>Total Requests</Typography></CardContent></StatCard>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard status="pending" elevation={0}><CardContent><FaClock size={32} color="#ff9800" style={{ marginBottom: 10 }} /><Typography variant="h3" fontWeight={700} color="#ef6c00">{stats.pending}</Typography><Typography variant="body2" color="#ef6c00" fontWeight={600}>Pending Approval</Typography></CardContent></StatCard>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard status="approved" elevation={0}><CardContent><FaCheckCircle size={32} color="#4caf50" style={{ marginBottom: 10 }} /><Typography variant="h3" fontWeight={700} color="#2e7d32">{stats.approved}</Typography><Typography variant="body2" color="#2e7d32" fontWeight={600}>Approved</Typography></CardContent></StatCard>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard status="fulfilled" elevation={0}><CardContent><FaTruckLoading size={32} color="#9c27b0" style={{ marginBottom: 10 }} /><Typography variant="h3" fontWeight={700} color="#7b1fa2">{stats.fulfilled}</Typography><Typography variant="body2" color="#7b1fa2" fontWeight={600}>Fulfilled</Typography></CardContent></StatCard>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2, boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
          <TextField placeholder="Search by item or employee..." value={search} onChange={(e) => setSearch(e.target.value)} variant="outlined" size="small" InputProps={{ startAdornment: <InputAdornment position="start"><FaSearch style={{ color: "#757575" }} /></InputAdornment> }} sx={{ flex: 1, "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "background.default" } }} />
          <TextField select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} variant="outlined" size="small" sx={{ minWidth: 150, "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "background.default" } }}>
            <MenuItem value="all">All Status</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="approved">Approved</MenuItem>
            <MenuItem value="rejected">Rejected</MenuItem>
            <MenuItem value="fulfilled">Fulfilled</MenuItem>
          </TextField>
        </Stack>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Table */}
      <StyledTableContainer>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: "#fafafa" }}>
              <StyledTableCell width={60}>SL</StyledTableCell>
              <StyledTableCell>Item & Details</StyledTableCell>
              <StyledTableCell>Employee</StyledTableCell>
              <StyledTableCell align="center">Requested</StyledTableCell>
              <StyledTableCell align="center">Approved</StyledTableCell>
              <StyledTableCell align="center">Fulfilled</StyledTableCell>
              <StyledTableCell>Urgency</StyledTableCell>
              <StyledTableCell>Status</StyledTableCell>
              <StyledTableCell align="center">Actions</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.length === 0 ? (
              <TableRow><TableCell colSpan={9} align="center" sx={{ py: 8 }}><Box sx={{ textAlign: "center" }}><FaBoxOpen style={{ fontSize: 48, color: "#bdbdbd", marginBottom: 16 }} /><Typography variant="h6" color="text.secondary" gutterBottom>No requisitions found</Typography></Box></TableCell></TableRow>
            ) : (
              paginatedData.map((row, index) => {
                const statusLower = row.status?.toLowerCase();
                return (
                  <StyledTableRow key={row.id || index} hover>
                    <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                    <TableCell>
                      <Typography variant="subtitle2" fontWeight={600}>{row.item_name}</Typography>
                      <Typography variant="caption" color="text.secondary">{row.purpose || "No purpose provided"}</Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Avatar sx={{ width: 32, height: 32, fontSize: "0.8rem", bgcolor: "#667eea" }}>{row.employee?.employee_name?.charAt(0) || "E"}</Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={500}>{row.employee?.employee_name || "N/A"}</Typography>
                          <Typography variant="caption" color="text.secondary">{row.department || "N/A"}</Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell align="center"><Typography variant="body2" fontWeight={600}>{row.requested_quantity} {row.unit}</Typography></TableCell>
                    <TableCell align="center"><Typography variant="body2" fontWeight={600} color={row.approved_quantity ? "#2e7d32" : "text.secondary"}>{row.approved_quantity || "-"} {row.unit}</Typography></TableCell>
                    <TableCell align="center"><Typography variant="body2" fontWeight={600} color={row.fulfilled_quantity ? "#1565c0" : "text.secondary"}>{row.fulfilled_quantity || "-"} {row.unit}</Typography></TableCell>
                    <TableCell>{getUrgencyChip(row.urgency_level)}</TableCell>
                    <TableCell>{getStatusChip(row.status)}</TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        <Tooltip title="View Details"><IconButton size="small" onClick={() => openActionModal("view", row)} sx={{ color: "#2196f3" }}><FaEye fontSize="small" /></IconButton></Tooltip>
                        {statusLower === "pending" && (
                          <>
                            <Tooltip title="Approve"><ActionButton variant="approve" size="small" disabled={actionLoading === row.id} onClick={() => openActionModal("approve", row)} startIcon={<FaCheck />}>Approve</ActionButton></Tooltip>
                            <Tooltip title="Reject"><ActionButton variant="reject" size="small" disabled={actionLoading === row.id} onClick={() => openActionModal("reject", row)} startIcon={<FaTimes />}>Reject</ActionButton></Tooltip>
                          </>
                        )}
                        {statusLower === "approved" && (
                          <Tooltip title="Fulfill (Deduct Inventory)"><ActionButton variant="fulfill" size="small" disabled={actionLoading === row.id} onClick={() => openActionModal("fulfill", row)} startIcon={<FaTruckLoading />}>Fulfill</ActionButton></Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </StyledTableRow>
                );
              })
            )}
          </TableBody>
        </Table>
        {filtered.length > 0 && (
          <TablePagination rowsPerPageOptions={[10, 25, 50, 100]} component="div" count={filtered.length} rowsPerPage={rowsPerPage} page={page} onPageChange={handleChangePage} onRowsPerPageChange={handleChangeRowsPerPage} sx={{ borderTop: 1, borderColor: "divider" }} />
        )}
      </StyledTableContainer>

      {/* Action Modals */}
      <Dialog open={actionModal.open} onClose={() => setActionModal({ open: false, type: "", data: null })} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ bgcolor: actionModal.type === "approve" ? "#4caf50" : actionModal.type === "reject" ? "#f44336" : "#667eea", color: "white", py: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {actionModal.type === "approve" ? <FaCheckCircle /> : actionModal.type === "reject" ? <FaTimesCircle /> : actionModal.type === "fulfill" ? <FaTruckLoading /> : <FaEye />}
            {actionModal.type === "view" ? "Requisition Details" : `${actionModal.type.charAt(0).toUpperCase() + actionModal.type.slice(1)} Requisition`}
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {actionModal.data && (
            <Stack spacing={2.5}>
              {actionModal.type === "view" ? (
                <>
                  <Box><Typography variant="subtitle2" color="text.secondary">Item</Typography><Typography variant="body1" fontWeight={600}>{actionModal.data.item_name}</Typography></Box>
                  <Box><Typography variant="subtitle2" color="text.secondary">Employee</Typography><Typography variant="body1" fontWeight={600}>{actionModal.data.employee?.employee_name} ({actionModal.data.department})</Typography></Box>
                  <Grid container spacing={2}>
                    <Grid item xs={4}><Typography variant="subtitle2" color="text.secondary">Requested</Typography><Typography variant="body1" fontWeight={600}>{actionModal.data.requested_quantity} {actionModal.data.unit}</Typography></Grid>
                    <Grid item xs={4}><Typography variant="subtitle2" color="text.secondary">Approved</Typography><Typography variant="body1" fontWeight={600}>{actionModal.data.approved_quantity || "-"} {actionModal.data.unit}</Typography></Grid>
                    <Grid item xs={4}><Typography variant="subtitle2" color="text.secondary">Fulfilled</Typography><Typography variant="body1" fontWeight={600}>{actionModal.data.fulfilled_quantity || "-"} {actionModal.data.unit}</Typography></Grid>
                  </Grid>
                  <Box><Typography variant="subtitle2" color="text.secondary">Purpose</Typography><Typography variant="body1">{actionModal.data.purpose}</Typography></Box>
                  {actionModal.data.admin_notes && <Alert severity="info"><strong>Admin Notes:</strong> {actionModal.data.admin_notes}</Alert>}
                  {actionModal.data.rejection_reason && <Alert severity="error"><strong>Rejection Reason:</strong> {actionModal.data.rejection_reason}</Alert>}
                </>
              ) : (
                <>
                  <Alert severity="info"><strong>Item:</strong> {actionModal.data.item_name} | <strong>Requested:</strong> {actionModal.data.requested_quantity} {actionModal.data.unit}</Alert>

                  {(actionModal.type === "approve" || actionModal.type === "fulfill") && (
                    <TextField
                      fullWidth
                      label={actionModal.type === "approve" ? "Approved Quantity" : "Fulfilled Quantity"}
                      type="number"
                      value={actionForm.quantity}
                      onChange={(e) => setActionForm({ ...actionForm, quantity: e.target.value })}
                      inputProps={{ min: 1 }}
                      helperText={actionModal.type === "approve" ? "You can approve less than requested (e.g., requested 5, approve 3)" : "This will deduct from inventory"}
                    />
                  )}

                  {actionModal.type === "approve" && (
                    <TextField fullWidth label="Admin Notes (Optional)" multiline rows={2} value={actionForm.notes} onChange={(e) => setActionForm({ ...actionForm, notes: e.target.value })} placeholder="e.g., Approved 3 units due to low stock" />
                  )}

                  {actionModal.type === "reject" && (
                    <TextField fullWidth label="Rejection Reason *" multiline rows={3} value={actionForm.reason} onChange={(e) => setActionForm({ ...actionForm, reason: e.target.value })} placeholder="Please explain why this is rejected" required error={!actionForm.reason.trim()} helperText={!actionForm.reason.trim() ? "Reason is required" : ""} />
                  )}
                </>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5, pt: 1 }}>
          <Button onClick={() => setActionModal({ open: false, type: "", data: null })} variant="outlined" sx={{ textTransform: "none", fontWeight: 600, px: 3, borderRadius: 2 }}>Cancel</Button>
          {actionModal.type !== "view" && (
            <Button variant="contained" onClick={submitAction} disabled={actionLoading === actionModal.data?.id} sx={{ textTransform: "none", fontWeight: 600, px: 3, borderRadius: 2, bgcolor: actionModal.type === "approve" ? "#4caf50" : actionModal.type === "reject" ? "#f44336" : "#667eea" }}>
              {actionLoading === actionModal.data?.id ? "Processing..." : `Confirm ${actionModal.type.charAt(0).toUpperCase() + actionModal.type.slice(1)}`}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RequisitionApproval;